import { Anchor, Box, Flex } from "@govlaunch/web";
import Link from "next/link";
import React from "react";
import { useSelfie } from "~/components/auth/Auth";
import useSignInModal from "~/src/components/auth/useSignInModal";
import { ICardViewMode } from "~/src/components/collection/cardViewMode";
import getInitialTabRedirect from "~/src/components/collection/getInitialTabRedirect";
import { IListAllCollectionsQuery } from "~/src/components/collection/graphql/__generated__/ListCollectionsQuery.generated";
import BundleBookmarkButton from "~/src/components/collection/bundleCard/BundleBookmarkButton";
import BundleCardBackground from "~/src/components/collection/bundleCard/BundleCardBackground";
import BundleCardForeground from "~/src/components/collection/bundleCard/BundleCardForeground";
import BundleCollectionCardName from "~/src/components/collection/bundleCard/BundleCollectionCardName";
import BundleCollectionCardNewContent from "~/src/components/collection/bundleCard/BundleCollectionCardNewContent";
import BundleCollectionCardTags from "~/src/components/collection/bundleCard/BundleCollectionCardTags";
import BundleCollectionCardTotal from "~/src/components/collection/bundleCard/BundleCollectionCardTotal";

type TCollection = IListAllCollectionsQuery["listAllCollections"]["items"][0];

interface IBundleCollectionCardProps {
  collection: TCollection;
  onToggleBookmark?: (collection: TCollection) => void;
  viewMode?: ICardViewMode;
}

export default function BundleCollectionCard({
  collection,
  viewMode = ICardViewMode.Bundles,
  onToggleBookmark,
}: IBundleCollectionCardProps) {
  const selfie = useSelfie();
  const signInModal = useSignInModal();

  const totalContentCount =
    collection.contentCounters.groupsCount +
    collection.contentCounters.productsCount +
    collection.contentCounters.projectsCount +
    collection.contentCounters.storiesCount +
    collection.contentCounters.governmentsCount;

  const newContentCount = collection.newContentCounters
    ? collection.newContentCounters.groupsCount +
      collection.newContentCounters.productsCount +
      collection.newContentCounters.projectsCount +
      collection.newContentCounters.storiesCount +
      collection.newContentCounters.governmentsCount
    : 0;

  const redirect = getInitialTabRedirect(collection.contentCounters);

  let linkHref = "/collections/[collectionSlug]/[[...tab]]";
  let linkAs = `/collections/${collection.slug}/projects`;

  if (redirect) {
    linkHref = "/collections/[collectionSlug]/[[...tab]]";
    linkAs = `/collections/${collection.slug}/${redirect}`;
  }

  return (
    <Link href={linkHref} as={linkAs} passHref={true}>
      <Anchor
        border="none"
        borderColor="transparent"
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="center"
        width={["100%", "100%", "100%", viewMode === ICardViewMode.Bundles ? "100%" : "226px"]}
        height={
          viewMode === ICardViewMode.Bundles ? "280px" : viewMode === ICardViewMode.MyCollections ? "224px" : "148px"
        }
      >
        <BundleCardBackground />
        <Box
          position="relative"
          data-intercom-target="Collection card"
          width="100%"
          height="100%"
          borderColor="#73849D !important"
          border="1px"
          borderRadius="12px 12px 4px 12px"
          bg="white"
          overflow="hidden"
          boxShadow="lg"
          _hover={{
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
          }}
        >
          {onToggleBookmark && (
            <BundleBookmarkButton
              collection={collection}
              selfie={selfie}
              signInModal={signInModal}
              onToggleBookmark={onToggleBookmark}
            />
          )}

          <BundleCardForeground color={collection.displayOptions?.cardBackgroundColorHex} viewMode={viewMode} />

          <Flex
            px={viewMode === ICardViewMode.Bundles || viewMode === ICardViewMode.MyCollections ? 5 : 2}
            position="relative"
            flexDirection="column"
            alignItems="center"
          >
            <BundleCollectionCardName
              viewMode={viewMode}
              color={collection.displayOptions?.cardTitleFontColorHex}
              name={collection.name}
            />

            {(viewMode === ICardViewMode.Bundles || viewMode === ICardViewMode.MyCollections) && (
              <BundleCollectionCardTags tags={collection.tags} viewMode={viewMode} />
            )}
          </Flex>

          {newContentCount > 0 && <BundleCollectionCardNewContent viewMode={viewMode} />}

          <BundleCollectionCardTotal viewMode={viewMode} total={totalContentCount} />
        </Box>
      </Anchor>
    </Link>
  );
}
